<script lang="ts">
  import Typography from "$lib/components/Typography/Typography.svelte";
  import Button from "../Button/Button.svelte";
  import Grid from "$lib/components/Grid/Grid.svelte";
  import Container from "../Container/Container.svelte";
  import type { Link } from "$lib/gql/genCms/codegen";
  import type { Position } from "$lib/gql/genCms/codegen";
  import { processInlineLinks } from "$lib/utils/url";
  import Divider from "$lib/components/Divider/Divider.svelte";

  export let title: string;
  export let text: string;
  export let align: Position;
  export let link1: Link;
  export let link2: Link;
  export let ctaOneBgColour: string;
  export let ctaTwoBgColour: string;
  export let ctaOneTextLight = false;
  export let ctaTwoTextLight = false;
  export let headingElement = "h1";

  let viewportWidth: number;
  let isMobile = false;
  let mdWidth = 768;

  $: isMobile = viewportWidth < mdWidth;

  export let headingVariant = isMobile ? "h2" : "h1";
  export let rectangleDivider = false;
</script>

<style lang="scss">
  .text-with-cta {
    border-bottom: 1px solid $background;

    @include retina {
      border-bottom: 0.5px solid $background;
    }
  }

  .wrapper {
    grid-column: span 4;
    text-align: center;
    padding: 48px 0;

    &--LEFT,
    &--RIGHT {
      text-align: left;

      .buttons {
        justify-content: flex-start;
        margin-left: -12px;
      }
    }
  }

  .title + .description {
    margin-top: 30px;
  }

  .title + .buttons {
    margin-top: 39px;
  }

  .description + .buttons {
    margin-top: 39px;
  }

  .buttons {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
  }

  .button {
    margin: 0 12px 24px;
    &:last-child {
      margin-bottom: 0;
    }
  }

  @include breakpoint(md) {
    .wrapper {
      padding: 64px 0;
      grid-column: 2 / span 6;

      &--LEFT {
        grid-column-start: auto;
      }

      &--RIGHT {
        grid-column: 3 / span 6;
      }
    }
  }

  @include breakpoint(lg) {
    .wrapper {
      padding: 96px 0;
      grid-column: 3 / span 8;

      &--LEFT {
        grid-column-start: auto;
      }

      &--RIGHT {
        grid-column: 5 / span 8;
      }
    }

    .description + .buttons {
      margin-top: 40px;
    }

    .button {
      margin-bottom: 0;
    }
  }
</style>

<svelte:window bind:innerWidth={viewportWidth} />

<div class="text-with-cta">
  <Container data-cs-override-id="text-with-cta">
    <Grid smCols={4} mdCols={8} lgCols={12}>
      <div class="wrapper wrapper--{align}">
        {#if title}
          <div class="title">
            <Typography variant={headingVariant} component={headingElement}
              >{title}</Typography
            >
          </div>
        {/if}
        {#if rectangleDivider}
          <Divider textPosition={align} textWithCta />
        {/if}
        {#if text}
          <div class="description">
            <Typography variant="body1-alt" component="span">
              {@html processInlineLinks(text)}
            </Typography>
          </div>
        {/if}

        {#if (link1 && link1 !== null) || (link2 && link2 !== null)}
          <div class="buttons">
            {#if link1 !== null && link1.href && link1.text}
              <div class="button">
                <Button
                  href={link1.href}
                  label={link1.text}
                  primary
                  theme={ctaOneTextLight
                    ? "inverse-on-surface"
                    : !ctaOneTextLight
                      ? "on-surface"
                      : "inverse-on-surface"}
                  backgroundColor={ctaOneBgColour
                    ? ctaOneBgColour
                    : "inverse-on-surface"}
                  hoverTheme={ctaOneTextLight
                    ? "on-surface"
                    : !ctaOneTextLight
                      ? "on-surface"
                      : "inverse-on-surface"}
                  hoverBgColor="transparent"
                />
              </div>
            {/if}
            {#if link2 !== null && link2.href && link2.text}
              <div class="button">
                <Button
                  href={link2.href}
                  label={link2.text}
                  primary
                  theme={ctaTwoTextLight
                    ? "inverse-on-surface"
                    : !ctaTwoTextLight
                      ? "on-surface"
                      : "inverse-on-surface"}
                  backgroundColor={ctaTwoBgColour
                    ? ctaTwoBgColour
                    : "inverse-on-surface"}
                  hoverTheme={ctaTwoTextLight
                    ? "on-surface"
                    : !ctaTwoTextLight
                      ? "on-surface"
                      : "inverse-on-surface"}
                  hoverBgColor="transparent"
                />
              </div>
            {/if}
          </div>
        {/if}
      </div>
    </Grid>
  </Container>
</div>
